<template>
  <b-modal
    id="add-customer-vehicle-modal"
    modal-class="add-cv-modal"
    :title="modalTitle"
    size="lg"
    no-close-on-backdrop
    :busy="submitting"
    :ok-disabled="submitting"
    @ok="onSubmit"
    @hidden="reset"
  >
    <!-- Chế độ SỬA: chỉ sửa thuộc tính xe, không đụng chủ xe -->
    <b-alert v-if="editingId" show variant="light" class="py-2 px-3 mb-3 cv-edit-note">
      <i class="fas fa-info-circle mr-1"></i>
      Đang sửa <b>hồ sơ xe</b> — chủ xe giữ nguyên. Chỉ cập nhật thông tin xe bên dưới.
    </b-alert>

    <!-- Loại khách hàng -->
    <b-form-group v-if="!editingId" label="Loại khách hàng">
      <b-form-radio-group
        v-model="form.customerType"
        :options="customerTypeOptions"
      />
    </b-form-group>

    <!-- Cá nhân -->
    <b-row v-if="!editingId && isIndividual">
      <b-col md="6">
        <label class="m-label"
          >Số điện thoại <span class="text-danger">*</span></label
        >
        <Autosuggest
          :model="form.phoneNumber"
          :suggestions="customerOptions"
          suggestionName="phoneNumber"
          placeholder="số điện thoại"
          :limit="10"
          @select="onSelectCustomer"
          @change="onCustomerPhoneChange"
        >
          <template #custom="{ suggestion }">
            <div class="d-flex flex-column">
              <span
                v-if="suggestion.item.phoneNumber"
                class="text-primary mb-1"
                >({{ suggestion.item.phoneNumber }})</span
              >
              <span v-if="suggestion.item.fullName">{{
                suggestion.item.fullName
              }}</span>
              <span v-else class="text-muted">{{
                suggestion.item.message
              }}</span>
            </div>
          </template>
        </Autosuggest>
        <small v-if="lockCustomerFields" class="text-success">
          Dùng lại KH đã có
          <b-link class="ml-1" @click="clearSelectedCustomer">Nhập KH mới</b-link>
        </small>
      </b-col>
      <b-col md="6">
        <label class="m-label">Họ tên <span class="text-danger">*</span></label>
        <b-form-input
          v-model="form.fullName"
          size="sm"
          :readonly="lockCustomerFields"
        />
      </b-col>
      <b-col md="6">
        <label class="m-label">Email</label>
        <b-form-input
          v-model="form.email"
          size="sm"
          :readonly="lockCustomerFields"
        />
      </b-col>
      <b-col md="6">
        <label class="m-label">Địa chỉ</label>
        <b-form-input
          v-model="form.address"
          size="sm"
          :readonly="lockCustomerFields"
        />
      </b-col>
    </b-row>

    <!-- Doanh nghiệp -->
    <b-row v-else-if="!editingId">
      <b-col md="6">
        <label class="m-label"
          >Mã số thuế <span class="text-danger">*</span></label
        >
        <Autosuggest
          :model="form.taxNo"
          :suggestions="businessOptions"
          suggestionName="taxNo"
          placeholder="mã số thuế"
          :limit="10"
          @select="onSelectBusiness"
          @change="onBusinessTaxChange"
        >
          <template #custom="{ suggestion }">
            <div class="d-flex flex-column">
              <span v-if="suggestion.item.taxNo" class="text-primary mb-1"
                >({{ suggestion.item.taxNo }})</span
              >
              <span v-if="suggestion.item.name">{{
                suggestion.item.name
              }}</span>
              <span v-else class="text-muted">{{
                suggestion.item.message
              }}</span>
            </div>
          </template>
        </Autosuggest>
        <small v-if="lockBusinessFields" class="text-success">
          Dùng lại DN đã có
          <b-link class="ml-1" @click="clearSelectedBusiness">Nhập DN mới</b-link>
        </small>
      </b-col>
      <b-col md="6">
        <label class="m-label"
          >Tên doanh nghiệp <span class="text-danger">*</span></label
        >
        <b-form-input
          v-model="form.businessName"
          size="sm"
          :readonly="lockBusinessFields"
        />
      </b-col>
      <b-col md="6">
        <label class="m-label">Người đại diện</label>
        <b-form-input
          v-model="form.representative"
          size="sm"
          :readonly="lockBusinessFields"
        />
      </b-col>
      <b-col md="6">
        <label class="m-label">Số điện thoại</label>
        <b-form-input
          v-model="form.phoneNumber"
          size="sm"
          :readonly="lockBusinessFields"
        />
      </b-col>
      <b-col md="6">
        <label class="m-label">Địa chỉ</label>
        <b-form-input
          v-model="form.address"
          size="sm"
          :readonly="lockBusinessFields"
        />
      </b-col>
    </b-row>

    <hr v-if="!editingId" />

    <!-- Xe -->
    <b-row>
      <b-col md="6">
        <label class="m-label"
          >Biển số xe <span class="text-danger">*</span></label
        >
        <b-form-input v-model="form.licensePlate" size="sm" />
      </b-col>
      <b-col md="6">
        <label class="m-label"
          >Dòng xe <span class="text-danger">*</span></label
        >
        <multiselect
          v-model="selectedProduct"
          :options="productOptions"
          :loading="productLoading"
          :internal-search="false"
          :clear-on-select="true"
          :close-on-select="true"
          :show-labels="false"
          :show-no-results="true"
          track-by="id"
          label="label"
          placeholder="Chọn / tìm model xe"
          open-direction="bottom"
          @search-change="onProductSearch"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            <div class="cv-ms-single">
              <span class="cv-ms-name">{{ option.name }}</span>
              <span v-if="option.code" class="cv-ms-code">{{
                option.code
              }}</span>
            </div>
          </template>
          <template slot="option" slot-scope="{ option }">
            <div class="cv-ms-option">
              <span class="cv-ms-name">{{ option.name }}</span>
              <span v-if="option.code" class="cv-ms-code">{{
                option.code
              }}</span>
            </div>
          </template>
          <span slot="noResult">Không có kết quả</span>
          <span slot="noOptions">Nhập để tìm model xe</span>
        </multiselect>
      </b-col>
      <b-col md="6" class="mt-2">
        <b-form-checkbox v-model="form.isSoldByShowroom">
          Xe do showroom bán ra
        </b-form-checkbox>
      </b-col>
      <b-col md="6" class="mt-2">
        <label class="m-label">Công ty BH thân vỏ (gốc của xe)</label>
        <b-form-select
          v-model="form.insuranceCompanyId"
          size="sm"
          :options="insuranceOptions"
        />
      </b-col>
    </b-row>

    <b-button
      variant="link"
      size="sm"
      class="px-0"
      @click="expanded = !expanded"
    >
      {{ expanded ? 'Thu gọn' : 'Mở rộng' }}
    </b-button>
    <b-row v-if="expanded">
      <b-col md="4">
        <label class="m-label">Số khung</label>
        <b-form-input v-model="form.chassisNumber" size="sm" />
      </b-col>
      <b-col md="4">
        <label class="m-label">Số máy</label>
        <b-form-input v-model="form.engineNumber" size="sm" />
      </b-col>
      <b-col md="4">
        <label class="m-label">Màu xe</label>
        <b-form-input v-model="form.color" size="sm" />
      </b-col>
      <b-col md="4">
        <label class="m-label">Năm sản xuất</label>
        <b-form-input v-model="form.manufactureYear" type="number" size="sm" />
      </b-col>
      <b-col md="4">
        <label class="m-label">Số km</label>
        <b-form-input v-model="form.odometer" type="number" size="sm" />
      </b-col>
      <b-col md="4">
        <label class="m-label">Hạn bảo hành</label>
        <b-form-input v-model="form.warrantyEndDate" type="date" size="sm" />
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import debounce from 'debounce';
import { get } from 'lodash';
import productService from '@/core/services/product';
import ServiceOrderService from '@/core/services/serviceOrder.service';
import InsuranceService from '@/core/services/insurance.service';
import { CustomerService } from '@/core/services/customers';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { makeToastFaile } from '@/utils/common';
import { PRODUCT_TYPE_ENUM } from '@/utils/enum';
import { TIME_TRIGGER } from '@/utils/constants';

const emptyForm = () => ({
  customerType: 'CUSTOMER',
  fullName: '',
  phoneNumber: '',
  email: '',
  address: '',
  businessName: '',
  taxNo: '',
  representative: '',
  licensePlate: '',
  isSoldByShowroom: false,
  insuranceCompanyId: null,
  chassisNumber: '',
  engineNumber: '',
  color: '',
  manufactureYear: '',
  odometer: '',
  warrantyEndDate: '',
});

export default {
  name: 'AddCustomerVehicleModal',
  components: { Multiselect, Autosuggest },
  data() {
    return {
      // null = chế độ THÊM; có id = chế độ SỬA hồ sơ xe.
      editingId: null,
      // Chặn bấm 2 lần: modal giữ nguyên sau preventDefault(), mạng chậm là click đúp bắn 2 POST →
      // tạo trùng KHÁCH HÀNG (không có unique trên customers.phoneNumber, và KH được tạo NGOÀI
      // transaction xe nên rollback xe không xoá KH). Xe thì DB đã chặn bằng unique activeLicensePlate.
      submitting: false,
      form: emptyForm(),
      selectedProduct: null,
      productOptions: [],
      productLoading: false,
      insuranceCompanies: [],
      expanded: false,
      // Tra KH theo SĐT (chọn lại KH cũ) — pattern giống module contract.
      customerOptions: [],
      selectedCustomerId: null,
      lockCustomerFields: false,
      // Tra DN theo MST (chọn lại DN cũ) — đổi "type" sang doanh nghiệp.
      businessOptions: [],
      selectedBusinessId: null,
      lockBusinessFields: false,
      customerTypeOptions: [
        { value: 'CUSTOMER', text: 'Cá nhân' },
        { value: 'BUSSINESS', text: 'Doanh nghiệp' },
      ],
    };
  },
  computed: {
    modalTitle() {
      return this.editingId ? 'Sửa hồ sơ xe' : 'Thêm khách hàng & xe';
    },
    isIndividual() {
      return this.form.customerType === 'CUSTOMER';
    },
    insuranceOptions() {
      return [
        { value: null, text: '-- Không --' },
        ...this.insuranceCompanies.map((c) => ({
          value: c.id,
          text: c.shortName || c.name,
        })),
      ];
    },
  },
  created() {
    this.onProductSearch = debounce(this.runProductSearch, 400);
    this.debounceCustomerSearch = debounce(
      this.fetchCustomerByPhone,
      TIME_TRIGGER,
    );
    this.debounceBusinessSearch = debounce(
      this.fetchBusinessByTax,
      TIME_TRIGGER,
    );
    this.loadInsurance();
  },
  methods: {
    show() {
      this.reset();
      this.$bvModal.show('add-customer-vehicle-modal');
    },
    // Mở modal ở chế độ SỬA — đổ sẵn thuộc tính xe từ `vehicle` (kết quả search). Chủ xe giữ nguyên.
    showEdit(vehicle) {
      this.reset();
      this.editingId = vehicle.id;
      this.expanded = true;
      this.form = {
        ...emptyForm(),
        customerType: vehicle.customerType || 'CUSTOMER',
        licensePlate: vehicle.licensePlate || '',
        isSoldByShowroom: !!vehicle.isSoldByShowroom,
        insuranceCompanyId: vehicle.insuranceCompanyId || null,
        chassisNumber: vehicle.chassisNumber || '',
        engineNumber: vehicle.engineNumber || '',
        color: vehicle.color || '',
        manufactureYear: vehicle.manufactureYear || '',
        odometer: vehicle.odometer != null ? vehicle.odometer : '',
        warrantyEndDate: this.toDateInput(vehicle.warrantyEndDate),
      };
      this.selectedProduct = vehicle.productId
        ? {
            id: vehicle.productId,
            name: vehicle.productName || '',
            code: '',
            label: vehicle.productName || vehicle.productId,
          }
        : null;
      this.$bvModal.show('add-customer-vehicle-modal');
    },
    // Chuẩn hoá ngày về 'YYYY-MM-DD' cho input type=date (nhận cả Date, ISO, 'YYYY-MM-DD').
    toDateInput(d) {
      return d ? String(d).slice(0, 10) : '';
    },
    reset() {
      this.editingId = null;
      this.submitting = false;
      this.form = emptyForm();
      this.selectedProduct = null;
      this.productOptions = [];
      this.expanded = false;
      this.customerOptions = [];
      this.selectedCustomerId = null;
      this.lockCustomerFields = false;
      this.businessOptions = [];
      this.selectedBusinessId = null;
      this.lockBusinessFields = false;
    },
    // @change của Autosuggest: gõ SĐT → reset lựa chọn cũ → debounce tra KH.
    onCustomerPhoneChange(text) {
      this.form.phoneNumber = text || '';
      this.selectedCustomerId = null;
      this.lockCustomerFields = false;
      this.customerOptions = [];
      if (!this.form.phoneNumber.trim()) return;
      this.debounceCustomerSearch();
    },
    // Tra KH theo SĐT (prefix) qua CustomerService. Không có → gợi ý "sẽ tạo KH mới".
    async fetchCustomerByPhone() {
      const phone = (this.form.phoneNumber || '').trim();
      if (!phone) return;
      try {
        const res = await CustomerService.getByPhone(phone);
        const list = get(res, 'data.data') || [];
        this.customerOptions = list.length
          ? list
          : [{ message: 'SĐT chưa có — sẽ tạo KH mới', phoneNumber: phone }];
      } catch (e) {
        this.customerOptions = [];
      }
    },
    // Chọn 1 gợi ý: có id → dùng lại KH cũ (điền sẵn + khoá field); message → KH mới.
    onSelectCustomer(option) {
      const item = option && option.item ? option.item : null;
      if (!item) return;
      if (item.id) {
        this.selectedCustomerId = item.id;
        this.form.phoneNumber = item.phoneNumber || this.form.phoneNumber;
        this.form.fullName = item.fullName || '';
        this.form.email = item.email || '';
        this.form.address = item.address || '';
        this.lockCustomerFields = true;
      } else {
        this.selectedCustomerId = null;
        this.lockCustomerFields = false;
        this.form.phoneNumber = item.phoneNumber || this.form.phoneNumber;
      }
    },
    // Bỏ chọn KH cũ → mở khoá field để nhập KH mới.
    clearSelectedCustomer() {
      this.selectedCustomerId = null;
      this.lockCustomerFields = false;
    },
    // @change của Autosuggest DN: gõ MST → reset lựa chọn cũ → debounce tra DN.
    onBusinessTaxChange(text) {
      this.form.taxNo = text || '';
      this.selectedBusinessId = null;
      this.lockBusinessFields = false;
      this.businessOptions = [];
      if (!this.form.taxNo.trim()) return;
      this.debounceBusinessSearch();
    },
    // Tra DN theo MST/tên/SĐT (LIKE) qua CustomerService. Không có → "sẽ tạo DN mới".
    async fetchBusinessByTax() {
      const text = (this.form.taxNo || '').trim();
      if (!text) return;
      try {
        const res = await CustomerService.searchBusiness(text);
        const payload = get(res, 'data.data');
        let list = [];
        if (Array.isArray(payload)) list = payload;
        else if (payload && Array.isArray(payload.data)) list = payload.data;
        this.businessOptions = list.length
          ? list
          : [{ message: 'DN chưa có — sẽ tạo DN mới', taxNo: text }];
      } catch (e) {
        this.businessOptions = [];
      }
    },
    // Chọn 1 gợi ý DN: có id → dùng lại DN cũ (điền sẵn + khoá field); message → DN mới.
    onSelectBusiness(option) {
      const item = option && option.item ? option.item : null;
      if (!item) return;
      if (item.id) {
        this.selectedBusinessId = item.id;
        this.form.taxNo = item.taxNo || this.form.taxNo;
        this.form.businessName = item.name || '';
        this.form.representative = item.representative || '';
        this.form.phoneNumber = item.phoneNo || '';
        this.form.address = item.address || '';
        this.lockBusinessFields = true;
      } else {
        this.selectedBusinessId = null;
        this.lockBusinessFields = false;
        this.form.taxNo = item.taxNo || this.form.taxNo;
      }
    },
    // Bỏ chọn DN cũ → mở khoá field để nhập DN mới.
    clearSelectedBusiness() {
      this.selectedBusinessId = null;
      this.lockBusinessFields = false;
    },
    async loadInsurance() {
      try {
        const res = await InsuranceService.getCompanies({ limit: 200 });
        const body = (res.data && res.data.data) || [];
        this.insuranceCompanies = Array.isArray(body) ? body : body.data || [];
      } catch (e) {
        this.insuranceCompanies = [];
      }
    },
    // Map 1 product (BE) → option cho multiselect (tên chính, mã phụ).
    toProductOption(p) {
      const name = p.name || '';
      const code = p.code || '';
      return {
        id: p.id,
        name,
        code,
        label: code ? `${code} - ${name}` : name || p.id,
      };
    },
    // @search-change → debounce 400ms (gán ở created) gọi API products (chỉ model xe).
    async runProductSearch(query) {
      const text = (query || '').trim();
      if (!text) {
        this.productOptions = [];
        this.productLoading = false;
        return;
      }
      this.productLoading = true;
      try {
        const { data } = await productService.getProducts({
          page: 1,
          limit: 50,
          search: text,
          productType: PRODUCT_TYPE_ENUM.CHASSIS_ENGINE,
        });
        const rows = get(data, 'data.data') || get(data, 'data') || [];
        this.productOptions = Array.isArray(rows)
          ? rows.map((p) => this.toProductOption(p))
          : [];
      } catch (e) {
        this.productOptions = [];
      } finally {
        this.productLoading = false;
      }
    },
    validate() {
      // Chế độ SỬA: không đụng chủ xe → bỏ qua validate KH.
      if (!this.editingId) {
        if (this.isIndividual) {
          if (!this.form.fullName || !this.form.phoneNumber) {
            makeToastFaile('Cá nhân: cần Họ tên + SĐT');
            return false;
          }
        } else if (!this.form.businessName || !this.form.taxNo) {
          makeToastFaile('Doanh nghiệp: cần Tên DN + MST');
          return false;
        }
      }
      if (!this.form.licensePlate) {
        makeToastFaile('Cần nhập biển số xe');
        return false;
      }
      if (!this.selectedProduct) {
        makeToastFaile('Cần chọn dòng xe');
        return false;
      }
      return true;
    },
    // Payload SỬA — field null = xoá, gửi đủ các thuộc tính xe (không gồm chủ xe).
    buildUpdatePayload() {
      return {
        licensePlate: this.form.licensePlate,
        productId: this.selectedProduct ? this.selectedProduct.id : undefined,
        isSoldByShowroom: this.form.isSoldByShowroom,
        insuranceCompanyId: this.form.insuranceCompanyId || null,
        chassisNumber: this.form.chassisNumber || null,
        engineNumber: this.form.engineNumber || null,
        color: this.form.color || null,
        manufactureYear: this.form.manufactureYear
          ? Number(this.form.manufactureYear)
          : null,
        odometer:
          this.form.odometer !== '' && this.form.odometer != null
            ? Number(this.form.odometer)
            : null,
        warrantyEndDate: this.form.warrantyEndDate || null,
      };
    },
    buildPayload() {
      return {
        customerType: this.form.customerType,
        customerId: this.selectedCustomerId || undefined,
        customerBusinessId: this.selectedBusinessId || undefined,
        fullName: this.form.fullName || undefined,
        phoneNumber: (this.form.phoneNumber || '').trim() || undefined,
        email: this.form.email || undefined,
        address: this.form.address || undefined,
        businessName: this.form.businessName || undefined,
        taxNo: this.form.taxNo || undefined,
        representative: this.form.representative || undefined,
        licensePlate: this.form.licensePlate,
        productId: this.selectedProduct.id,
        isSoldByShowroom: this.form.isSoldByShowroom,
        insuranceCompanyId: this.form.insuranceCompanyId || undefined,
        chassisNumber: this.form.chassisNumber || undefined,
        engineNumber: this.form.engineNumber || undefined,
        color: this.form.color || undefined,
        manufactureYear: this.form.manufactureYear
          ? Number(this.form.manufactureYear)
          : undefined,
        odometer: this.form.odometer ? Number(this.form.odometer) : undefined,
        warrantyEndDate: this.form.warrantyEndDate || undefined,
      };
    },
    async onSubmit(bvModalEvt) {
      bvModalEvt.preventDefault();
      // Guard ĐỒNG BỘ — phải có kể cả khi đã :ok-disabled, vì Vue cập nhật DOM ở nextTick nên click đúp
      // đủ nhanh vẫn kịp vào đây 2 lần trước lúc nút bị vô hiệu hoá.
      if (this.submitting) return;
      if (!this.validate()) return;
      this.submitting = true;
      try {
        // Chế độ SỬA hồ sơ xe.
        if (this.editingId) {
          const res = await ServiceOrderService.updateCustomerVehicle(
            this.editingId,
            this.buildUpdatePayload(),
          );
          const vehicle = (res.data && res.data.data) || {};
          this.$emit('updated', {
            ...vehicle,
            productName: this.selectedProduct ? this.selectedProduct.name : '',
          });
          this.$bvModal.hide('add-customer-vehicle-modal');
          return;
        }
        // Chế độ THÊM mới KH + xe.
        const res = await ServiceOrderService.createCustomerVehicle(
          this.buildPayload(),
        );
        const vehicle = (res.data && res.data.data) || {};
        // Bổ sung nhãn để panel hiển thị (BE trả entity không kèm tên SP/KH).
        this.$emit('created', {
          ...vehicle,
          productName: this.selectedProduct.name,
          customerName: this.isIndividual
            ? this.form.fullName
            : this.form.businessName,
          customerPhone: this.form.phoneNumber,
        });
        this.$bvModal.hide('add-customer-vehicle-modal');
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          'Lưu khách hàng / xe thất bại';
        makeToastFaile(msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.m-label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 2px;
  display: block;
}
</style>

<!-- Override vue-multiselect theo Metronic — KHÔNG scoped để chạm class nội bộ;
     prefix .add-cv-modal (modal-class) để không ảnh hưởng multiselect khác. -->
<style lang="scss">
.add-cv-modal {
  // Slot model: tên là chính, mã nhỏ/mono phụ.
  .cv-ms-option,
  .cv-ms-single {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    .cv-ms-name {
      font-size: 13px;
      font-weight: 500;
      color: #3f4254;
      white-space: normal;
      word-break: break-word;
      overflow-wrap: anywhere;
    }
    .cv-ms-code {
      font-size: 11px;
      color: #b5b5c3;
      font-family: 'SF Mono', Menlo, Consolas, monospace;
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.02em;
    }
  }
  .multiselect {
    min-height: 31px;
  }
  .multiselect__tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    min-height: 31px;
    height: auto;
    padding: 2px 40px 2px 8px;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    font-size: 13px;
  }
  .multiselect__input,
  .multiselect__single {
    font-size: 13px;
    line-height: 1.3;
    margin-bottom: 0;
    padding: 0;
    background: transparent;
    white-space: normal;
  }
  .multiselect__input {
    flex: 0 1 auto;
    min-width: 0;
  }
  .multiselect__placeholder {
    font-size: 13px;
    color: #b5b5c3;
    padding: 0;
    margin-bottom: 0;
    line-height: 1.3;
  }
  .multiselect__select {
    height: 29px;
  }
  .multiselect__content-wrapper {
    background: #fff;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    z-index: 60;
  }
  .multiselect__option {
    min-height: auto;
    padding: 8px 12px;
    font-size: 13px;
  }
  .multiselect__option--highlight {
    background: #f3f6f9;
    color: #3f4254;
  }
  .multiselect__option--highlight::after {
    display: none;
  }
  .multiselect__option--selected {
    background: #ebedf3;
    color: #3f4254;
    font-weight: 500;
  }
  .multiselect__option--selected.multiselect__option--highlight {
    background: #e1e3ea;
    color: #3f4254;
  }
}
</style>
